import React, { useMemo, useState, useEffect } from 'react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { CTASection } from '../components/CTASection';
import { FestivalAgendaSection } from '../components/FestivalAgendaSection';
import { FestivalFilters } from '../components/FestivalFilters';
import { FestivalMapPlaceholder } from '../components/FestivalMapPlaceholder';
import { FestivalViewToggle } from '../components/FestivalViewToggle';
import {
  festivals,
  getApproxVisitorsValue,
  getMonthLabelFromDate,
  months,
  regions,
} from '../data/festivals';

const FestivalsDirectoryPage = () => {
  const [search, setSearch] = useState('');
  const [region, setRegion] = useState('Alle');
  const [month, setMonth] = useState('Alle');
  const [sort, setSort] = useState('earliest');
  const [view, setView] = useState('calendar');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const availableMonths = useMemo(() => {
    const monthSet = new Set(festivals.map((festival) => getMonthLabelFromDate(festival.startDate)));
    return ['Alle', ...months.filter((monthName) => monthName !== 'Alle' && monthSet.has(monthName))];
  }, []);

  const filteredFestivals = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    const result = festivals.filter((festival) => {
      const matchesSearch =
        !normalizedSearch ||
        festival.name.toLowerCase().includes(normalizedSearch) ||
        festival.city.toLowerCase().includes(normalizedSearch);

      const matchesRegion = region === 'Alle' || festival.region === region;
      const matchesMonth =
        month === 'Alle' ||
        getMonthLabelFromDate(festival.startDate) === month;

      return matchesSearch && matchesRegion && matchesMonth;
    });

    return result.sort((a, b) => {
      if (sort === 'latest') {
        return new Date(b.startDate) - new Date(a.startDate);
      }

      if (sort === 'alphabetical') {
        return a.name.localeCompare(b.name, 'da');
      }

      if (sort === 'largest') {
        const visitorsA = getApproxVisitorsValue(a.approxVisitors);
        const visitorsB = getApproxVisitorsValue(b.approxVisitors);
        return visitorsB - visitorsA;
      }

      return new Date(a.startDate) - new Date(b.startDate);
    });
  }, [month, region, search, sort]);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <main>
        <section className="relative overflow-hidden bg-primary pt-32 pb-20 md:pt-40 md:pb-24">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: "url('/festivalfoto.jpg')" }}
          />
          <div className="absolute inset-0 bg-primary/70" />
          <div className="container-custom relative z-10">
            <div className="max-w-4xl">
              <h1 className="font-heading text-4xl font-bold leading-tight text-white sm:text-5xl lg:text-6xl">
                Festival kalender
              </h1>
              <p className="mt-4 max-w-3xl text-lg text-white/78 md:text-xl">
                Find danske festivaler, filtrér efter landsdel og få hurtigt overblik over datoer, byer og officielle links.
              </p>
            </div>
          </div>
        </section>

        <FestivalFilters
          search={search}
          onSearchChange={setSearch}
          region={region}
          onRegionChange={setRegion}
          month={month}
          onMonthChange={setMonth}
          sort={sort}
          onSortChange={setSort}
          regions={regions}
          months={availableMonths}
        />

        <FestivalViewToggle view={view} onViewChange={setView} />

        {view === 'calendar' ? (
          <FestivalAgendaSection festivals={filteredFestivals} />
        ) : (
          <FestivalMapPlaceholder festivals={filteredFestivals} />
        )}

        <CTASection
          href="mailto:kasper@museiq.io?subject=Demo%20af%20MUSEIQ"
        />
      </main>

      <div className="h-10 bg-white md:h-14" />
      <Footer />
    </div>
  );
};

export default FestivalsDirectoryPage;
